import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { UploadSimple, Calculator } from "@phosphor-icons/react";

const PCT_FIELDS = [
  ["basic_pct_of_ctc", "Basic (% of CTC)"], ["hra_pct_of_basic", "HRA (% of Basic)"], ["annual_bonus_pct_of_basic", "Annual bonus (% of Basic)"],
  ["performance_bonus_pct_of_ctc", "Performance bonus (% of CTC)"], ["pf_pct", "PF (% of Basic)"], ["pf_wage_ceiling_monthly", "PF wage ceiling / month"],
  ["esic_employer_pct", "ESIC employer %"], ["esic_employee_pct", "ESIC employee %"], ["esic_gross_ceiling_monthly", "ESIC gross ceiling / month"],
  ["professional_tax_monthly", "Professional tax / month"], ["ot_deduction_monthly", "OT deduction / month"],
];

export function UploadTemplateButton({ onLoaded }) {
  const ref = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/letter-ctc/upload-template", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onLoaded(data);
      const custom = data.detected_fields.filter((f) => !f.auto).map((f) => f.key);
      toast.success(`Template loaded · ${data.detected_fields.length} field(s) detected${custom.length ? ` · custom: ${custom.join(", ")}` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
    if (ref.current) ref.current.value = "";
  };
  return (
    <>
      <input ref={ref} type="file" accept=".docx,.txt,.html,.htm,.md" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="studio-upload-input" />
      <Button variant="outline" onClick={() => ref.current?.click()} className="rounded-sm" title="Upload your own letter (.docx / .txt) — fields like [Name], <<Position>>, {{ctc_table}} are detected automatically" data-testid="studio-upload-btn">
        <UploadSimple size={16} className="mr-1" /> Upload template
      </Button>
    </>
  );
}

export function CtcTableDialog({ params, onInsert }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [annual, setAnnual] = useState(1200000);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => { if (open && !cfg) api.get("/letter-ctc/config", { params }).then(({ data }) => setCfg(data.config)).catch((e) => toast.error(formatApiError(e))); }, [open, cfg, params]);
  useEffect(() => {
    if (!cfg) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.post("/letter-ctc/compute", { ...params, annual_ctc: Number(annual) || 0, overrides: cfg }).then(({ data }) => setRes(data)).catch(() => {});
    }, 350);
    return () => clearTimeout(timer.current);
  }, [cfg, annual, params]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/letter-ctc/config", { ...params, config: cfg }); setCfg(data.config); toast.success("CTC formula saved for all letters"); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-sm" data-testid="studio-ctc-btn"><Calculator size={16} className="mr-1" /> CTC table</Button></DialogTrigger>
      <DialogContent className="max-w-4xl" data-testid="ctc-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Formula-driven CTC table</DialogTitle></DialogHeader>
        <div className="text-xs text-zinc-500">Insert <code className="font-mono bg-zinc-100 px-1">{"{{ ctc_table }}"}</code> anywhere in a letter. At issue time the table is computed from the employee's <b>annual_ctc</b> (or <b>new_ctc</b>) variable — falling back to their salary structure — using the formulas below. Every component (Basic, HRA, allowances, bonuses, PF, ESIC, PT, OT, take-home) updates automatically.</div>
        {!cfg ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Formulas</div>
              {PCT_FIELDS.map(([k, l]) => (
                <div key={k} className="flex items-center justify-between gap-2"><Label className="text-[11px]">{l}</Label><Input type="number" step="0.01" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className="rounded-sm h-7 w-24 text-xs font-mono text-right" data-testid={`ctc-cfg-${k}`} /></div>
              ))}
              <div className="flex items-center justify-between"><Label className="text-[11px]">Apply PF wage ceiling</Label><Switch checked={!!cfg.pf_apply_ceiling} onCheckedChange={(v) => setCfg({ ...cfg, pf_apply_ceiling: v })} data-testid="ctc-cfg-pf-ceiling" /></div>
              <div className="flex items-center justify-between"><Label className="text-[11px]">Show monthly column</Label><Switch checked={!!cfg.show_monthly} onCheckedChange={(v) => setCfg({ ...cfg, show_monthly: v })} /></div>
              <div className="flex items-center justify-between"><Label className="text-[11px]">Show annual column</Label><Switch checked={!!cfg.show_annual} onCheckedChange={(v) => setCfg({ ...cfg, show_annual: v })} /></div>
              <div className="flex items-center justify-between"><Label className="text-[11px]">Show net take-home</Label><Switch checked={!!cfg.show_take_home} onCheckedChange={(v) => setCfg({ ...cfg, show_take_home: v })} /></div>
              <div className="flex items-center justify-between gap-2"><Label className="text-[11px]">Currency label</Label><Input value={cfg.currency} onChange={(e) => setCfg({ ...cfg, currency: e.target.value })} className="rounded-sm h-7 w-24 text-xs" data-testid="ctc-cfg-currency" /></div>
            </div>
            <div className="md:col-span-3 space-y-2">
              <div className="flex items-center gap-2"><Label className="text-xs">Sample annual CTC</Label><Input type="number" value={annual} onChange={(e) => setAnnual(e.target.value)} className="rounded-sm h-8 w-40 font-mono text-sm" data-testid="ctc-sample-annual" />{res && <Badge variant="outline" className="rounded-sm">Monthly {res.monthly_ctc.toLocaleString("en-IN")}</Badge>}</div>
              <div className="border border-zinc-200 rounded-sm p-2 bg-white text-xs [&_table]:w-full [&_td]:py-1 [&_td]:px-2 [&_th]:py-1 [&_th]:px-2 [&_th]:bg-zinc-100 [&_td]:border-b [&_td]:border-zinc-100" data-testid="ctc-preview" dangerouslySetInnerHTML={{ __html: res?.html || "" }} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={save} disabled={busy || !cfg} className="rounded-sm" data-testid="ctc-save">{busy ? "Saving…" : "Save formulas"}</Button>
          <Button onClick={() => { setOpen(false); setTimeout(onInsert, 150); }} className="btn-zenith rounded-sm" data-testid="ctc-insert">Insert {"{{ ctc_table }}"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
